import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import DetailDrawer from './DetailDrawer'
import type { Switches, BrowserSessionDetail } from '../../api/types'
import { useSession } from '../../api/session'
import BrowserSettings from './BrowserSettings'

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export default function KillSwitch() {
  const { user } = useSession()
  const admin = user?.role === 'admin'
  const [s, setS] = useState<Switches | null>(null)
  const [pending, setPending] = useState<{ scope: string; enabled: boolean; label: string } | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [details, setDetails] = useState<{title: string; sessions: BrowserSessionDetail[]} | null>(null)

  useEffect(() => {
    let live = true
    const refresh = () => api.switches().then(value => { if (live) setS(value) }).catch(e => { if (live) setError(e.message) })
    void refresh()
    const timer = window.setInterval(refresh, 5000)
    return () => { live = false; window.clearInterval(timer) }
  }, [])

  async function confirm() {
    if (!pending || !admin || busy || !reason.trim()) return
    setBusy(true)
    try {
      const next = await api.setSwitch(pending.scope, pending.enabled, reason.trim())
      setS(next); setPending(null); setReason(''); setError('')
    } catch(e) { setError(e instanceof Error ? e.message : 'Could not update controls') }
    finally { setBusy(false) }
  }

  if (!s) return error ? <div className="notice crit">{error}</div> : <p className="muted">Loading…</p>

  const off = !s.global.enabled

  return (
    <div className="controls-page">
      {error && <div className="notice crit" role="alert">{error}</div>}
      <div className="app-head">
        <h1>Controls</h1>
      </div>

      {!admin && <p className="notice">Read-only access. An administrator can change outbound and browser settings.</p>}

      <div className={`switch-card${off ? ' off' : ''}`}>
        <span className="eyebrow">Global kill switch</span>
        <h2>
          <span className={`dot${off ? ' off' : ''}`} aria-hidden="true" />
          Outbound is {off ? 'off' : 'on'}
        </h2>
        <p style={{ fontSize: '0.9rem' }}>
          {off
            ? `Email, callback calls, and form submissions are paused. ${s.paused_jobs} queued workflows will resume when outbound is enabled. ${s.held_jobs} blocked workflows need review. Preparation-only jobs can continue.`
            : 'Contact workflows check this control before dispatch. A request already accepted by a provider may still complete after you pause outbound.'}
        </p>
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Last changed by {s.global.changed_by} · {when(s.global.changed_at)}
          {s.global.reason ? ` · “${s.global.reason}”` : ''}
        </p>
        {admin && <div className="action-group">
          {off ? (
            <button className="btn" disabled={busy} onClick={() => { setReason(''); setPending({ scope: 'global', enabled: true, label: 'Turn outbound back on' }) }}>
              Turn outbound on
            </button>
          ) : (
            <button className="btn danger" disabled={busy} onClick={() => { setReason(''); setPending({ scope: 'global', enabled: false, label: 'Stop all outbound' }) }}>
              Stop all outbound
            </button>
          )}
        </div>}
      </div>

      {admin && pending && (
        <form className="confirm" aria-labelledby="confirm-title" onSubmit={event => { event.preventDefault(); void confirm() }}>
          <strong id="confirm-title">{pending.label}</strong>
          <div className="field">
            <label htmlFor="reason">Reason (logged with your name and the time)</label>
            <input id="reason" className="input" required maxLength={500} disabled={busy} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </div>
          <div className="action-group">
            <button className={`btn${pending.enabled ? '' : ' danger'}`} type="submit" disabled={!reason.trim() || busy}>
              {busy ? 'Saving…' : 'Confirm'}
            </button>
            <button type="button" className="btn secondary" disabled={busy} onClick={() => { setPending(null); setReason('') }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <BrowserSettings />
      <section className="controls-health-section">
      <h2>Trips</h2>
      <p className="muted">Past automatic shutdowns. Browser failures no longer trigger shutdowns.</p>
      {!(s.trip_history || []).length && <p className="muted">No recorded trips.</p>}
      <div className="controls-trip-list">{(s.trip_history || []).map((trip, i) => <div key={i}>
        <button className="controls-trip-row" onClick={() => setDetails({title: trip.rule, sessions: trip.sessions})}>
          <span>{trip.rule}</span><time>{when(trip.created_at)}</time><span className="muted">{trip.current}</span><span aria-hidden="true">›</span>
        </button>
      </div>)}</div>
      </section>
      <section className="controls-health-section">
      <h2>Warnings</h2>
      <div className="table-wrap controls-health-table">
        <table className="data">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Warns at</th>
              <th>Current</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {s.trips.map((t) => (
              <tr key={t.rule}>
                <td className="wrap"><button className="health-detail-button" onClick={() => setDetails({title: t.rule, sessions: t.sessions})}>{t.rule}</button></td>
                <td className="wrap">{t.threshold}</td>
                <td className="mono">{t.current}</td>
                <td>{t.tripped ? <span className="pill crit">warning</span> : <span className="pill ok">clear</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted controls-health-note">
        Browser health uses the last hour of completed sessions. These warnings never pause outbound. Each company’s issue remains available in Diagnostics.
      </p>
      </section>
      {details && <DetailDrawer title={details.title} onClose={() => setDetails(null)}>
        {!details.sessions.length && <p className="muted">No sessions in this reporting window.</p>}
        {details.sessions.map(session => <section key={session.job_id} style={{borderBottom: '1px solid var(--line)', paddingBottom: '1rem', marginBottom: '1rem'}}>
          <h3><a href={`/app/companies/${session.company_id}`}>{session.company_name}</a></h3>
          <p>{session.status} · {when(session.updated_at)}</p>
          {session.error && <p style={{overflowWrap: 'anywhere'}}>{session.error}</p>}
          {session.replay_url ? <a href={session.replay_url} target="_blank" rel="noreferrer">Open Browserbase replay ↗</a> : <p className="muted">No replay was recorded for this session.</p>}
          <details><summary>Step logs · {session.steps.length}</summary><pre style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{JSON.stringify(session.steps, null, 2)}</pre></details>
        </section>)}
      </DetailDrawer>}
    </div>
  )
}
